import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MapPin, RotateCcw, Truck, Trash2, ChevronDown, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { getRecentOrders, summarizeOrder, clearRecentOrders } from '../utils/recentOrders';
import { loadLatestOrder, getCurrentStepIndex, STEP_ORDER, type TrackedOrder } from '../utils/orderTracking';
// The personalized greeting replaces the marketing hero headline +
// tagline once the visitor has built up a relationship with us:
//
//   - first-time           → no saved order anywhere on the device
//                            → show the regular marketing hero
//   - just-delivered-once  → 1st return visit after a delivered order
//                            → still show the marketing hero, but the
//                              existing small WelcomeBackCard pill greets
//                              them and offers one-tap reorder
//   - repeat               → 2nd+ visit after a delivered order
//                            → drop the marketing pitch and the small
//                              pill; replace with a personal "Good
//                              morning, {firstName}" hero
//   - in-flight            → there's an active order from this device
//                            that hasn't reached the "delivered" step
//                            yet → personal greeting with a track CTA
//                            instead of a reorder CTA (regardless of
//                            visit count — this is the more urgent,
//                            more useful surface in that moment)
//
// All state is derived from localStorage so the system works without
// any auth. On a new device / private window the visitor naturally
// falls back to 'first-time', which is fine.
type HeroVariant = 'first-time' | 'just-delivered-once' | 'repeat' | 'in-flight';
const LAST_ORDER_KEY = '253gravel_last_order';
const VISITS_KEY = '253gravel_visits';
type SavedOrder = {
  items: {
    id?: string;
    category: string;
    product: string;
    quantity: number;
    pricePerYard: number;
  }[];
  addressInput?: string;
  name?: string;
  phone?: string;
  email?: string;
};
function getFirstName(fullName?: string): string | null {
  if (!fullName) return null;
  // First whitespace-separated token, stripped of trailing punctuation
  // and capped at ~16 chars so an obscure all-lowercase or all-caps
  // submission doesn't make the headline look broken. Reject single
  // letters and emoji-only inputs — they read as obviously wrong.
  const token = fullName.trim().split(/\s+/)[0] || '';
  const cleaned = token.replace(/[^\p{L}\p{M}'\-]/gu, '');
  if (cleaned.length < 2) return null;
  // Normalise casing: capital first letter, lowercase rest. Leaves
  // names with internal capitalisation (e.g. "McKay") alone if they
  // came in that way.
  const isAllSameCase = cleaned === cleaned.toLowerCase() || cleaned === cleaned.toUpperCase();
  const display = isAllSameCase ? cleaned[0].toUpperCase() + cleaned.slice(1).toLowerCase() : cleaned;
  return display.slice(0, 16);
}
function timeOfDayGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Hi';
}
// Rotating subhead questions for the repeat-visitor variant. Each phrase
// asks the same thing ("how can we help?") but in a different voice, so
// returning customers don't see the exact same line every visit. One is
// picked at mount time, so it's stable while the user is on the page
// but refreshes on the next visit.
const REPEAT_GREETING_QUESTIONS = ['What can we get you today?', 'What are we hauling for you today?', 'Where are we delivering this round?', 'Ready for another load?', "What's the next project?"];
function pickRepeatGreetingQuestion(): string {
  const i = Math.floor(Math.random() * REPEAT_GREETING_QUESTIONS.length);
  return REPEAT_GREETING_QUESTIONS[i];
}
export function usePersonalGreeting(): {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
} {
  const [variant, setVariant] = useState<HeroVariant>('first-time');
  const [firstName, setFirstName] = useState<string | null>(null);
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  const [activeOrder, setActiveOrder] = useState<TrackedOrder | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) {
        setVariant('first-time');
        return;
      }
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) {
        setVariant('first-time');
        return;
      }
      setSavedOrder(parsed);
      setFirstName(getFirstName(parsed.name));
      // In-flight order? Same source the WelcomeBackCard uses so the
      // two surfaces stay in lockstep.
      const latest = loadLatestOrder();
      if (latest) {
        const stepIdx = getCurrentStepIndex(latest);
        if (stepIdx < STEP_ORDER.length - 1) {
          setActiveOrder(latest);
          setActiveStepIdx(stepIdx);
          setVariant('in-flight');
          return;
        }
      }
      // No active order — pick between just-delivered-once and repeat
      // based on the same visits counter the WelcomeBackCard maintains.
      // `visits >= 2` means we have already given the customer at least
      // one shot at the small reorder pill on a prior visit, so it's
      // time to retire it and let the big greeting take the hero slot.
      const visits = parseInt(localStorage.getItem(VISITS_KEY) || '1', 10);
      setVariant(visits >= 2 ? 'repeat' : 'just-delivered-once');
    } catch {
      // Any localStorage / JSON issue → treat as first-time and let the
      // default marketing hero render. Failing closed never hurts here.
      setVariant('first-time');
    }
  }, []);
  return {
    variant,
    firstName,
    savedOrder,
    activeOrder,
    activeStepIdx
  };
}
// Friendly label for the active-order step. Matches the same vocab the
// /track page and WelcomeBackCard use so the customer sees consistent
// language across every touchpoint.
function stepLabelFor(stepIdx: number): string {
  switch (STEP_ORDER[stepIdx]) {
    case 'confirmed':
      return 'Order confirmed';
    case 'scheduled':
      return 'Scheduled for delivery';
    case 'dispatched':
      return 'Dispatched from yard';
    case 'en_route':
      return 'Driver on the way';
    default:
      return 'In progress';
  }
}
export function PersonalGreeting({
  variant,
  firstName,
  savedOrder,
  activeOrder,
  activeStepIdx,
  estimatorOpen = false,
  isDark = false
}: {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
  estimatorOpen?: boolean;
  isDark?: boolean;
}) {
  const {
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // When the estimator is open in LIGHT mode the hero backdrop fades to a
  // light fill, so the default white greeting copy goes invisible. In that
  // single case we invert the neutral text/glass to dark tones. Greens are
  // always left intact. Photo-background (estimator closed) and dark mode
  // both keep the original white treatment.
  const lightBg = estimatorOpen && !isDark;
  // Locked at first render so the phrase is stable while the user is on
  // the page — only a fresh page load rolls a new one.
  const [repeatQuestion] = useState<string>(() => pickRepeatGreetingQuestion());
  // Recent orders (up to 3) power the "Reorder last delivery" pulldown.
  // The collapsed tab shows the most recent; tapping the chevron reveals
  // the full list so the customer can reorder any of their standard loads.
  const [recentOrders, setRecentOrders] = useState<SavedOrder[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    setRecentOrders(getRecentOrders());
  }, []);
  // Wipe all saved order history, then reload so the hero recomputes back to
  // the default marketing greeting (first-time state).
  const handleClearHistory = () => {
    clearRecentOrders();
    if (typeof window !== 'undefined') window.location.reload();
  };
  if (variant !== 'in-flight' && variant !== 'repeat') return null;
  // Display name fallback — if we couldn't pull a clean first name from
  // the saved order (rare but possible: blank, single letter, emoji),
  // skip the name and lead with the generic time-of-day greeting alone.
  const greetingHead = firstName ? `${timeOfDayGreeting()}, ${firstName}` : timeOfDayGreeting();
  const handleReorder = (order?: SavedOrder) => {
    const target = order || recentOrders[0] || savedOrder;
    if (!target) return;
    loadSavedCart(target.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    if (target.addressInput) {
      setDeliveryInfo(target.addressInput, 0, null);
    }
    openCart();
  };
  // -------- IN-FLIGHT VARIANT --------
  // The customer just placed an order and the truck hasn't arrived yet.
  // The most useful surface in that moment is order status + a one-tap
  // jump to the live tracking page. Reorder is a secondary affordance.
  if (variant === 'in-flight' && activeOrder) {
    const stepLabel = stepLabelFor(activeStepIdx);
    const shortAddress = (activeOrder.address || '').split(',')[0].trim();
    const totalYards = activeOrder.items.reduce((s, i) => s + i.quantity, 0);
    const itemSummary = activeOrder.items.length > 1 ? `${totalYards} yds across ${activeOrder.items.length} materials` : `${activeOrder.items[0].quantity} yds ${activeOrder.items[0].product}`;
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }} className="mb-4 md:mb-6" data-id="element-1392" data-mp="sb1m3esvfvqa">
        <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-4 py-1.5 rounded-none bg-[#00754A]" data-id="element-1393" data-mp="eh0tz3wfd6yw">
          <span className="relative flex h-2 w-2" data-id="element-1394" data-mp="9ho8amhai5xw">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-1395" data-mp="9gwad507zw4g" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" data-id="element-1396" data-mp="4l7af0kagd7m" />
          </span>
          <span className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-white" data-id="element-1397" data-mp="3vrii3fjt0c7x">
            Order in progress
          </span>
          <span className="text-xs sm:text-sm font-medium normal-case tracking-normal text-white/75" data-id="element-1398" data-mp="1txsycm7dka31">
            · {stepLabel}
          </span>
        </div>

        <h1 className={`font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-3 md:mb-4 ${lightBg ? 'text-stone-900' : 'text-white'}`} data-id="element-1399" data-mp="22d8njqbtfkn5">
          {greetingHead}
          <span className="block mt-1 md:mt-2 italic font-medium tracking-normal" style={{
          fontFamily: '"Playfair Display", serif'
        }} data-id="element-1400" data-mp="2b9ps2apock9x">
            Your load is on the way.
          </span>
        </h1>

        {/* Delivery details: stacked on mobile so a long address doesn't
                                                                                                                                                                     break mid-word, collapsed onto a single row on desktop. White
                                                                                                                                                                     arrow reinforces the "material → destination" flow. */}
        <div className="mb-5 md:mb-6 max-w-2xl flex flex-col md:flex-row md:flex-wrap md:items-baseline gap-y-1.5 md:gap-x-3" data-id="element-1401" data-mp="2j66stf84c9j3">
          <p className={`text-lg md:text-xl font-semibold font-sub text-center md:text-left ${lightBg ? 'text-stone-700' : 'text-white/95'}`} data-id="element-1402" data-mp="2e7djlyzv74oq">
            {itemSummary}
          </p>
          {shortAddress && <p className={`text-lg md:text-xl font-semibold font-sub flex items-baseline gap-2 justify-center md:justify-start ${lightBg ? 'text-stone-700' : 'text-white/95'}`} data-id="element-1403" data-mp="2o6magizfo36v">
              <span className={`shrink-0 ${lightBg ? 'text-stone-700' : 'text-white'}`} aria-hidden="true" data-id="element-1404" data-mp="1vigoxu0k2a5z">
                →
              </span>
              <span data-id="element-1405" data-mp="2a84q8v8ql8tp">{shortAddress}</span>
            </p>}
        </div>

        <div className="flex flex-col items-center md:items-start gap-2.5 mb-2" data-id="element-1406" data-mp="25e0i48nqiur1">
          <Link to={`/track/${encodeURIComponent(activeOrder.orderId)}`} className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#04A05C] hover:bg-[#048F3A] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(4,160,92,0.35)]" aria-label="Track your delivery" data-id="element-1407" data-mp="1hopmt6p5lska">
            <MapPin className="w-4 h-4" data-id="element-1408" data-mp="3t6kftpa7k9xe" />
            Track delivery
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-1409" data-mp="3eguzunxgi1d2" />
          </Link>
          <span className={`text-sm text-center md:text-left ${lightBg ? 'text-stone-500' : 'text-white/70'}`} data-id="element-1410" data-mp="3jg7vny6b2irs">
            Need another load? Build a new order below.
          </span>
        </div>
      </motion.div>;
  }
  // -------- REPEAT VARIANT --------
  // No active order, but they've come back at least once since their
  // last delivery. They already know what we do; they're here to
  // buy again. Greet by name and put a reorder shortcut front and centre.
  if (!savedOrder) return null;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  }} className="mb-4 md:mb-6" data-id="element-1411" data-mp="2mo4bt0pxfiyw">
      <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-4 py-1.5 rounded-none bg-[#00754A]" data-id="element-1412" data-mp="36yozzrqro9zl">
        <Truck className="w-3.5 h-3.5 text-white" data-id="element-1413" data-mp="3265co32a20hl" />
        <span className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-white" data-id="element-1414" data-mp="2x7u37tfw9rzw">
          Welcome back
        </span>
      </div>

      <h1 className={`font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-3 md:mb-4 ${lightBg ? 'text-stone-900' : 'text-white'}`} data-id="element-1415" data-mp="2sa2a4w3ijzu4">
        {greetingHead}.
        <span className="block mt-1 md:mt-2 italic font-medium tracking-normal" style={{
        fontFamily: '"Playfair Display", serif'
      }} data-id="element-1416" data-mp="3kyq2tcqldrhp">
          {repeatQuestion}
        </span>
      </h1>

      <p className={`text-lg md:text-xl font-semibold font-sub mb-5 md:mb-6 max-w-2xl ${lightBg ? 'text-stone-700' : 'text-white/85'}`} data-id="element-1417" data-mp="3bvylwxey63io">
        Pick up where you left off — same address, same materials, or build a
        new order below.
      </p>

      {/* Style #4 — frosted-glass reorder pill. No header label. The pill
                                          shows the load in light green with a green "Reorder" link. A single
                                          order sits in the pill as one line ("15 yds Medium Bark to Kent" —
                                          city only, never the full street address). Multiple orders collapse
                                          into the pill and expand downward to reveal up to 3 recent loads. */}
      {(() => {
      const orders = recentOrders.length > 0 ? recentOrders : savedOrder ? [savedOrder] : [];
      if (orders.length === 0) return null;
      const hasMultiple = orders.length > 1;
      const headline = orders[0];
      // City only — never the full street address. Addresses look like
      // "123 Main St, Kent, WA 98032": the city is the 2nd comma segment
      // when present, otherwise fall back to the first segment.
      const cityOf = (order: SavedOrder): string => {
        const parts = (order.addressInput || '').split(',').map(p => p.trim()).filter(Boolean);
        if (parts.length >= 2) return parts[1];
        return parts[0] || '';
      };
      const orderLabel = (order: SavedOrder): string => {
        const city = cityOf(order);
        return city ? `${summarizeOrder(order)} to ${city}` : summarizeOrder(order);
      };
      // Frosted-glass surface + frosted-white top/bottom borders only (no
      // sides).
      const pillSurface = lightBg ? 'bg-stone-900/[0.04] border-stone-900/20' : 'bg-white/[0.07] border-white/25';
      // Faint-white order text (matches the border tone) instead of green.
      const orderTextTone = lightBg ? 'text-stone-700' : 'text-white/70';
      const subTone = lightBg ? 'text-stone-500' : 'text-white/55';
      // "Reorder" matches the faint order-info tone (no green) but stays
      // bold so it still reads as the actionable element.
      const reorderLink = `inline-flex items-center gap-1 text-sm font-bold shrink-0 transition-colors ${lightBg ? 'text-stone-700 hover:text-stone-900' : 'text-white/70 hover:text-white'}`;
      return <div className="flex justify-center md:justify-start" data-id="element-1418" data-mp="26wsbr0tuxshw">
            <div className={`w-full max-w-md border-y backdrop-blur-md overflow-hidden ${pillSurface}`} data-id="element-1419" data-mp="1s0t4fseknwdt">
              {/* Collapsed row. Leads with the small reorder icon. Multiple
                                    orders show "Your recent orders" + a "{n} recent" toggle;
                                    a single order shows its load label + a "Reorder" link. */}
              {hasMultiple ? <button type="button" onClick={() => setReorderOpen(v => !v)} aria-expanded={reorderOpen} className="group w-full flex items-center gap-2.5 px-4 py-2.5 text-left" data-id="element-1420" data-mp="19a33i98kajnl">
                  <RotateCcw className={`w-4 h-4 shrink-0 ${orderTextTone}`} data-id="element-1421" data-mp="1e71w2nckuijo" />
                  <span className={`text-sm sm:text-[15px] font-normal truncate min-w-0 flex-1 ${orderTextTone}`} data-id="element-1422" data-mp="109wrkrsihrqj">
                    Your recent orders
                  </span>
                  <span className={`${reorderLink} pointer-events-none`} data-id="element-1423" data-mp="1kpg59vm038mi">
                    {orders.length} recent
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${reorderOpen ? 'rotate-180' : ''}`} data-id="element-1424" data-mp="1frxqvpp7s174" />
                  </span>
                </button> : <div className="w-full flex items-center gap-2.5 px-4 py-2.5" data-id="element-1425" data-mp="15tqfrnhlwdd1">
                  {/* Single order: the load label leads, and the two icon
                            actions sit on the right — a reorder (circular arrow)
                            button that re-adds the load to the cart, and an "x"
                            that clears the saved order so the hero reverts to the
                            default marketing greeting. The "x" uses a two-step
                            confirm so a stray tap doesn't wipe history. */}
                  <span className={`text-sm sm:text-[15px] font-normal truncate min-w-0 flex-1 ${orderTextTone}`} data-id="element-1426" data-mp="i5qn2i0qicvt">
                    {orderLabel(headline)}
                  </span>
                  {confirmClear ? <span className="inline-flex items-center gap-2 shrink-0" data-id="element-1427" data-mp="2prgypgedayf4">
                      <span className={`text-[11px] ${subTone}`} data-id="element-1428" data-mp="2tper0s3fq0mq">Clear?</span>
                      <button type="button" onClick={() => setConfirmClear(false)} className={`text-[11px] font-semibold transition-colors ${lightBg ? 'text-stone-500 hover:text-stone-800' : 'text-white/60 hover:text-white'}`} data-id="element-1429" data-mp="2yr44sfi1ksei">
                        Cancel
                      </button>
                      <button type="button" onClick={handleClearHistory} className={`text-[11px] font-bold transition-colors ${lightBg ? 'text-red-600 hover:text-red-700' : 'text-red-300 hover:text-red-200'}`} data-id="element-1430" data-mp="2azqcg9jw4wtj">
                        Clear
                      </button>
                    </span> : <span className="inline-flex items-center gap-1 shrink-0" data-id="element-1431" data-mp="1nb6zvhtt5xvn">
                      <button type="button" onClick={() => handleReorder(headline)} aria-label={`Reorder ${orderLabel(headline)}`} title="Reorder" className={`group inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${lightBg ? 'text-stone-700 hover:text-stone-900 hover:bg-stone-900/[0.06]' : 'text-white/70 hover:text-white hover:bg-white/10'}`} data-id="element-1432" data-mp="1n92xx5fgz8y0">
                        <RotateCcw className="w-4 h-4 group-hover:-rotate-45 transition-transform" data-id="element-1433" data-mp="1a6iilqo3gfxv" />
                      </button>
                      <button type="button" onClick={() => setConfirmClear(true)} aria-label="Clear saved order" title="Clear saved order" className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${lightBg ? 'text-stone-400 hover:text-stone-700 hover:bg-stone-900/[0.06]' : 'text-white/40 hover:text-white/80 hover:bg-white/10'}`} data-id="element-1434" data-mp="hh36syoluj0w">
                        <X className="w-4 h-4" data-id="element-1435" data-mp="w7ajexrxl7to" />
                      </button>
                    </span>}
                </div>}

              {/* Expanded list — only for multiple orders. */}
              {hasMultiple && <AnimatePresence initial={false} data-id="element-1436" data-mp="3nkd9r2amkkl">
                  {reorderOpen && <motion.div key="reorder-drawer" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} className={`overflow-hidden border-t ${lightBg ? 'border-stone-900/10' : 'border-white/10'}`} role="list" aria-label="Recent orders" data-id="element-1437" data-mp="2gyn5o703k56g">
                      {orders.map((order, i) => <button key={i} type="button" role="listitem" onClick={() => {
                setReorderOpen(false);
                handleReorder(order);
              }} aria-label={`Reorder ${orderLabel(order)}`} className={`group w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b last:border-b-0 ${lightBg ? 'border-stone-900/[0.06] hover:bg-stone-900/[0.03]' : 'border-white/[0.06] hover:bg-white/[0.05]'}`} data-id="element-1438" data-mp="1t8j21zmws5fg" data-mpi={i}>
                          <span className={`text-[13px] font-normal truncate min-w-0 flex-1 ${orderTextTone}`} data-id="element-1439" data-mp="10mudwq559dcw">
                            {orderLabel(order)}
                          </span>
                          <span className={`${reorderLink} text-xs`} data-id="element-1440" data-mp="ro91lcq9mukk">
                            Reorder
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1441" data-mp="vpucwtn5j0sn" />
                          </span>
                        </button>)}

                      {/* Clear-history — only a small rounded tab docked to the
                                          right (no full-width footer bar). Two-step confirm. */}
                      <div className="flex justify-end px-2 py-1.5" data-id="element-1442" data-mp="1ennadgf0aiin">
                        {confirmClear ? <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 ${lightBg ? 'bg-stone-900/[0.05]' : 'bg-white/[0.06]'}`} data-id="element-1443" data-mp="1elanqpyazjz9">
                            <span className={`text-[11px] ${subTone}`} data-id="element-1444" data-mp="274re11wm1ggd">
                              Clear saved orders?
                            </span>
                            <button type="button" onClick={() => setConfirmClear(false)} className={`text-[11px] font-semibold transition-colors ${lightBg ? 'text-stone-500 hover:text-stone-800' : 'text-white/60 hover:text-white'}`} data-id="element-1445" data-mp="3mvjthyop6rte">
                              Cancel
                            </button>
                            <button type="button" onClick={handleClearHistory} className={`text-[11px] font-bold transition-colors ${lightBg ? 'text-red-600 hover:text-red-700' : 'text-red-300 hover:text-red-200'}`} data-id="element-1446" data-mp="38uzab1evetzd">
                              Clear
                            </button>
                          </span> : <button type="button" onClick={() => setConfirmClear(true)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${lightBg ? 'text-stone-400 hover:text-stone-600 hover:bg-stone-900/[0.04]' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]'}`} aria-label="Clear order history" data-id="element-1447" data-mp="3duvhtams13jo">
                            <Trash2 className="w-3 h-3" data-id="element-1448" data-mp="2yuzxroxt2l2z" />
                            Clear history
                          </button>}
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </div>
          </div>;
    })()}
    </motion.div>;
}